import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@beweegbaas/ui";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { StudentStatisticsSidebarFilters } from "#/modules/leerling.statistics/components/student-statistics-sidebar-filters";
import {
  leerlingStatisticsSearchDefaults,
  leerlingStatisticsSearchSchema,
} from "#/modules/leerling.statistics/filters.leerling-statistics";
import { getStudentStatistics } from "#/modules/school.students/students.functions";
import { DomeinChart } from "#/modules/util.charts/domein-chart";
import { LeerlijnChart } from "#/modules/util.charts/leerlijn-chart";
import {
  getLeerlijnTranslation,
  getSociaalEmotioneelDomeinTranslation,
} from "#/modules/util.i18n/enum-mapping";
import type { components } from "#/schema";
import { m } from "#/paraglide/messages";
import { isApiEnumValue } from "#/modules/util.helpers/zod-enum";
import { Leerjaar } from "#/schema.nswag";

const isLeerjaar = isApiEnumValue(Leerjaar);
export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/students/$id/statistics",
)({
  validateSearch: leerlingStatisticsSearchSchema,
  // Keeps default filter values out of the URL.
  search: { middlewares: [stripSearchParams(leerlingStatisticsSearchDefaults)] },
  loaderDeps: ({ search }) => ({
    selectedLeerlijnFilterIds: search.selectedLeerlijnFilterIds ?? [],
    selectedDomeinFilterIds: search.selectedDomeinFilterIds ?? [],
  }),
  loader: ({ params, deps }) =>
    getStudentStatistics({
      data: {
        leerlingId: params.id,
        selectedLeerlijnFilterIds: deps.selectedLeerlijnFilterIds,
        selectedDomeinFilterIds: deps.selectedDomeinFilterIds,
      },
    }),
  component: Statistics,
  errorComponent: ({ error }) => <ErrorBoundaryComponent error={error} />,
});

function Statistics() {
  const { snapshots, leerlijnFilter, domeinFilter, currentLeerjaar } = Route.useLoaderData();
  const search = Route.useSearch();

  const defaultLeerjaar: components["schemas"]["Leerjaar"] =
    currentLeerjaar && currentLeerjaar !== "None" ? currentLeerjaar : "Een";
  const selectedLeerjaar =
    search.leerjaar && isLeerjaar(search.leerjaar) ? search.leerjaar : defaultLeerjaar;

  const snapshot = snapshots.find((s) => s.leerjaar === selectedLeerjaar);

  // Both trees act as ONE page-wide filter: a selection in either tree hides the
  // section without a selection, so filtering on a leerlijn never leaves the page
  // showing only (unrelated) SE charts.
  const hasLeerlijnFilter = (search.selectedLeerlijnFilterIds ?? []).length > 0;
  const hasDomeinFilter = (search.selectedDomeinFilterIds ?? []).length > 0;
  const showLeerlijnen = hasLeerlijnFilter || !hasDomeinFilter;
  const showDomeinen = hasDomeinFilter || !hasLeerlijnFilter;

  const leerlijnen = showLeerlijnen ? (snapshot?.leerlijnen ?? []) : [];
  const domeinen = showDomeinen ? (snapshot?.domeinen ?? []) : [];

  return (
    <Page.SidebarLayout>
      <Page.Sidebar>
        <StudentStatisticsSidebarFilters
          leerlijnFilterTree={leerlijnFilter}
          domeinFilterTree={domeinFilter}
          snapshots={snapshots}
          defaultLeerjaar={defaultLeerjaar}
        />
      </Page.Sidebar>
      <div className="flex grow flex-col gap-6">
        {leerlijnen.length === 0 && domeinen.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m["student.statistics.empty_title"]()}</EmptyTitle>
              <EmptyDescription>{m["student.statistics.empty_description"]()}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <>
            {leerlijnen.map((leerlijn) => (
              <LeerlijnChart
                key={leerlijn.leerlijn}
                data={leerlijn}
                title={getLeerlijnTranslation(leerlijn.leerlijn)}
              />
            ))}
            {domeinen.map((domein) => (
              <DomeinChart
                key={domein.domein}
                data={domein}
                title={getSociaalEmotioneelDomeinTranslation(domein.domein)}
              />
            ))}
          </>
        )}
      </div>
    </Page.SidebarLayout>
  );
}
